import {
  Box,
  Button,
  Modal,
  RadioButton,
  RadioGroup,
  Text
} from "@modernatx/ui-kit-react";
import React from "react";

import { PaymentMethod } from "@/types/PaymentMethod";
import { getPaymentDetails } from "@/utils/InvoicePay";

import { PaymentForm } from "../PaymentForm";

interface PaymentMethodProps {
  allPaymentMethods: PaymentMethod[];
  defaultPaymentMethod: PaymentMethod;
  selectedPaymentMethod: PaymentMethod | undefined;
  setSelectedPaymentMethod: React.Dispatch<
    React.SetStateAction<PaymentMethod | undefined>
  >;
}

export const PaymentMethodCard: React.FC<PaymentMethodProps> = ({
  allPaymentMethods,
  defaultPaymentMethod,
  selectedPaymentMethod,
  setSelectedPaymentMethod
}) => {
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const [allMethods, setAllMethods] = React.useState(allPaymentMethods);

  const handleChange = () => {
    setIsExpanded(true);
  };

  const handleSelectionChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const selected = allMethods.find(
        (pm) => pm.paymentMethodInfo.lastFour === value
      );
      if (selected) {
        setSelectedPaymentMethod(selected);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allMethods]
  );

  const sortedPaymentMethods = [
    defaultPaymentMethod,
    ...allMethods.filter(
      (pm) =>
        pm.paymentMethodInfo?.lastFour !==
        defaultPaymentMethod.paymentMethodInfo.lastFour
    )
  ];

  const paymentFormSubmitRef =
    React.useRef<() => Promise<PaymentMethod | undefined>>();

  const CollapsedView = () => {
    const brand = selectedPaymentMethod?.paymentMethodInfo?.brand;
    const bankName = selectedPaymentMethod?.paymentMethodInfo?.bankName;
    const lastFour = selectedPaymentMethod?.paymentMethodInfo?.lastFour;

    if (!brand && !bankName && !lastFour) return null;

    const paymentDetails = getPaymentDetails(brand, bankName);
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "spacing-6",
          gap: "spacing-4"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "spacing-4" }}>
          {paymentDetails.logo}
          <Text variant="body-1-regular">
            Paying with <Text variant="body-1-bold">{paymentDetails.name}</Text>{" "}
            ending in <Text variant="body-1-bold">{lastFour}</Text>
          </Text>
        </Box>

        <Button variant="outline" onClick={handleChange}>
          Change
        </Button>
      </Box>
    );
  };

  const [isAddPaymentOpen, setIsAddPaymentOpen] = React.useState(false);

  const handleCloseModal = () => {
    setIsAddPaymentOpen(false);
  };

  const handleAddPaymentMethod = async () => {
    const newPaymentMethod = await paymentFormSubmitRef.current?.();
    if (newPaymentMethod) {
      setAllMethods((prev) => [...prev, newPaymentMethod]);
      setIsAddPaymentOpen(false);
      setIsExpanded(false);
      setSelectedPaymentMethod(newPaymentMethod);
    }
  };

  const AddPaymentMethodModal = () => {
    const modalButtons = [
      { label: "Cancel", onClick: () => handleCloseModal() },
      {
        label: "Add payment method",
        onClick: () => {
          handleAddPaymentMethod();
        }
      }
    ];
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: "100%",
          backdropFilter: "blur(4px)",
          zIndex: 1
        }}
      >
        <Modal
          title="Add a payment method"
          isOpen={isAddPaymentOpen}
          buttons={modalButtons}
          onDismiss={handleCloseModal}
        >
          <PaymentForm
            setSubmitRef={(fn) => {
              paymentFormSubmitRef.current = fn;
            }}
            source="modal"
          />
        </Modal>
      </Box>
    );
  };

  const paymentMethodOptions = sortedPaymentMethods.map((pm) => {
    const { brand, bankName, lastFour, expirationDate, billingName } =
      pm.paymentMethodInfo;
    const details = getPaymentDetails(brand, bankName);

    const PaymentMethodLabel = (
      <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Text variant="body-1-bold">{details.name}</Text>
          <Text>ending in</Text>
          <Text variant="body-1-bold">{lastFour}</Text>
        </Box>

        {billingName && <Text>{billingName}</Text>}

        {brand && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Text>Expires </Text>
            <Text variant="body-1-regular">{expirationDate}</Text>
          </Box>
        )}
      </Box>
    );

    return (
      <RadioButton key={lastFour} label={PaymentMethodLabel} value={lastFour} />
    );
  });

  const ExpandedView = () => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: "spacing-4",
          padding: "spacing-4",
          alignItems: "flex-start",
          flexDirection: "column"
        }}
      >
        <RadioGroup
          label="Your payment methods"
          value={selectedPaymentMethod?.paymentMethodInfo.lastFour}
          onChange={handleSelectionChange}
        >
          {paymentMethodOptions}
        </RadioGroup>

        <Box>
          <Button
            onClick={() => {
              setIsExpanded(false);
              setSelectedPaymentMethod(selectedPaymentMethod);
            }}
          >
            Use this payment method
          </Button>
          <Button
            variant="subtle"
            onClick={() => {
              setIsAddPaymentOpen(true);
            }}
          >
            Add a new payment method
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          border: "1px solid",
          borderRadius: "radius-2",
          borderColor: "color-system-gray-200",
          justifyContent: "space-between",
          width: "100%"
        }}
      >
        {isExpanded ? <ExpandedView /> : <CollapsedView />}
      </Box>
      {isAddPaymentOpen && <AddPaymentMethodModal />}
    </>
  );
};
